// screens/personalShared.tsx — what the Personal tab's sub-tabs share: the
// rise-over sheet the calendar and lists write through, the wall-clock
// helpers, and the "who" words a shared row needs.
//
// The sheet is the MoreSheet/OutcomeSheet construction (fixed, scrim, box,
// Escape closes) — the settled shape in this app, not a new one.
import type { ComponentChildren } from 'preact';
import { useEffect, useRef } from 'preact/hooks';
import { ApiError } from '../lib/api';

export function PersonalSheet({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ComponentChildren;
}) {
  const boxRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    const first = boxRef.current?.querySelector('input, textarea, select');
    if (first instanceof HTMLElement) first.focus();
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);
  return (
    <div class="psheet" data-testid="personal-sheet">
      <button type="button" class="psheet__scrim" aria-label="Close" onClick={onClose} />
      <div class="psheet__box" ref={boxRef} role="dialog" aria-label={title}>
        <div class="psheet__grip" aria-hidden="true" />
        <h2 class="psheet__title">{title}</h2>
        {children}
      </div>
    </div>
  );
}

/** The server's own sentence when there was one (voice canon §2.4). */
export function failureOf(err: unknown): string {
  if (err instanceof ApiError) return err.serverMessage ?? err.message;
  return 'the request did not reach the server';
}

/** The viewer's own zone — what "3:30" means to the person typing it. */
export function localZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

const pad = (n: number) => String(n).padStart(2, '0');

/** `2026-10-08` for a Date, in the viewer's local calendar. */
export function localDate(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** `15:00` — the next whole hour from now. */
export function nextHour(now = new Date()): string {
  return `${pad((now.getHours() + 1) % 24)}:00`;
}

/** "Tue, Oct 13, 3:30 PM" from an offset-free wall clock (or a bare date). */
export function wallClockLabel(local: string | null | undefined, allDay = false): string {
  if (!local) return '';
  const date = local.slice(0, 10);
  if (allDay || local.length <= 10) {
    return new Date(`${date}T12:00:00`).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
  }
  return new Date(local.slice(0, 16)).toLocaleString([], {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** Who a row names: "you", the member's name, or nothing we would be
 * inventing. A service write says which service ("Mey · Coconup"). */
export function whoLabel(
  userId: string | null | undefined,
  createdVia: string | null | undefined,
  names: Record<string, string>,
  meId: string | null | undefined
): string | null {
  const who = userId ? (userId === meId ? 'you' : names[userId] ?? null) : null;
  const via = createdVia?.startsWith('service:') ? serviceWord(createdVia.slice('service:'.length)) : null;
  if (who && via) return `${who} · ${via}`;
  return who ?? via;
}

function serviceWord(name: string): string {
  return /coconup/i.test(name) ? 'Coconup' : name;
}
